import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, inject, Injectable, Injector } from "@angular/core";
import { DOCUMENT } from "@angular/common";

import { AzmToastComponent } from "src/app/shared/components/feedback/toast/toast.component";
import {
	AZM_TOAST_DEFAULT_CONFIG,
	AzmToastConfig,
	AzmToastRef,
	AzmToastType,
} from "src/app/shared/components/feedback/toast/models/toast.model";
import { AZM_TOAST_DATA, AzmToastInstanceData } from "src/app/shared/components/feedback/toast/models/toast.tokens";
import { elevateToastOverlay, keepToastOverlayElevated } from "src/app/shared/utilities/overlay/toast-overlay.helpers";
import { isAuthSessionEnding } from "../auth/auth-session-lifecycle.helper";

const MAX_OPENED = 3;
const SUPPRESSED_TOAST_REF: AzmToastRef = { toastId: 0, close: () => undefined };

@Injectable({ providedIn: "root" })
export class ToastService {
	readonly #appRef = inject(ApplicationRef);
	readonly #envInjector = inject(EnvironmentInjector);
	readonly #document = inject(DOCUMENT);

	#container: HTMLElement | null = null;
	#nextId = 1;
	readonly #instances = new Map<number, ComponentRef<AzmToastComponent>>();

	success(message: string, title?: string, config?: Partial<AzmToastConfig>): AzmToastRef {
		return this.#show("toast-success", message, title, config);
	}

	error(message: string, title?: string, config?: Partial<AzmToastConfig>, options?: { force?: boolean }): AzmToastRef {
		if (isAuthSessionEnding() && !options?.force) {
			return SUPPRESSED_TOAST_REF;
		}

		return this.#show("toast-error", message, title, config);
	}

	warning(message: string, title?: string, config?: Partial<AzmToastConfig>): AzmToastRef {
		if (isAuthSessionEnding()) {
			return SUPPRESSED_TOAST_REF;
		}

		return this.#show("toast-warning", message, title, config);
	}

	info(message: string, title?: string, config?: Partial<AzmToastConfig>): AzmToastRef {
		return this.#show("toast-info", message, title, config);
	}

	backendSuccess(title: string, config?: Partial<AzmToastConfig>): AzmToastRef {
		return this.#show("toast-success", "", title, config);
	}

	backendError(title: string, config?: Partial<AzmToastConfig>): AzmToastRef {
		if (isAuthSessionEnding()) {
			return SUPPRESSED_TOAST_REF;
		}

		return this.#show("toast-error", "", title, config);
	}

	backendWarning(title: string, config?: Partial<AzmToastConfig>): AzmToastRef {
		if (isAuthSessionEnding()) {
			return SUPPRESSED_TOAST_REF;
		}

		return this.#show("toast-warning", "", title, config);
	}

	clear(toastId?: number): void {
		if (toastId == null) {
			[...this.#instances.keys()].forEach((id) => this.#instances.get(id)?.instance.remove());
			return;
		}

		this.#instances.get(toastId)?.instance.remove();
	}

	#show(toastType: AzmToastType, message: string, title?: string, config?: Partial<AzmToastConfig>): AzmToastRef {
		const mergedConfig: Required<AzmToastConfig> = {
			...AZM_TOAST_DEFAULT_CONFIG,
			...config,
		};
		const toastId = this.#nextId++;
		const container = this.#ensureContainer();

		this.#enforceMaxOpened();

		const data: AzmToastInstanceData = {
			toastId,
			toastType,
			message,
			title,
			config: mergedConfig,
			onRemove: (id) => this.#destroy(id),
		};

		const ref = createComponent(AzmToastComponent, {
			environmentInjector: this.#envInjector,
			elementInjector: Injector.create({
				providers: [{ provide: AZM_TOAST_DATA, useValue: data }],
			}),
		});

		const host = ref.location.nativeElement as HTMLElement;
		host.classList.add("azm-toast-host", toastType, "toast-in");
		container.prepend(host);

		this.#appRef.attachView(ref.hostView);
		ref.changeDetectorRef.detectChanges();

		this.#instances.set(toastId, ref);

		return {
			toastId,
			close: () => ref.instance.remove(),
		};
	}

	#syncContainerClass(): void {
		this.#container?.classList.add("toast-container", "toast-top-right");
		this.#container?.classList.remove("toast-top-center");
	}

	#ensureContainer(): HTMLElement {
		keepToastOverlayElevated();

		if (this.#container?.isConnected) {
			this.#syncContainerClass();
			elevateToastOverlay();
			return this.#container;
		}

		const existing = this.#document.getElementById("toast-container");
		if (existing) {
			this.#container = existing;
			this.#syncContainerClass();
			elevateToastOverlay();
			return existing;
		}

		this.#container = this.#document.createElement("div");
		this.#container.id = "toast-container";
		this.#syncContainerClass();
		this.#document.body.appendChild(this.#container);
		elevateToastOverlay();

		return this.#container;
	}

	#enforceMaxOpened(): void {
		const active = [...this.#instances.values()].filter((ref) => !ref.instance.isRemoving());

		const removeCount = active.length - MAX_OPENED + 1;
		active.slice(0, Math.max(0, removeCount)).forEach((ref) => ref.instance.remove());
	}

	#destroy(toastId: number): void {
		const ref = this.#instances.get(toastId);
		if (!ref) {
			return;
		}

		this.#instances.delete(toastId);
		this.#appRef.detachView(ref.hostView);
		ref.destroy();
	}
}
